import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { withFirebase } from '../Firebase';
import * as ROUTES from '../../constants/routes';

const VerifyEmailActionPageBase = ({ firebase }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const handledCodeRef = useRef('');

  const oobCode = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get('oobCode') || '';
  }, [location.search]);

  useEffect(() => {
    let active = true;

    if (!oobCode) {
      setStatus('error');
      setError(t('verifyEmailAction.invalid_link'));
      return () => {
        active = false;
      };
    }

    if (handledCodeRef.current === oobCode) {
      return () => {
        active = false;
      };
    }

    handledCodeRef.current = oobCode;

    firebase.doApplyActionCode(oobCode)
      .then(() => {
        if (!active) return;
        setStatus('success');
        setError('');
      })
      .catch(actionError => {
        if (!active) return;
        setStatus('error');
        setError(actionError?.message || t('verifyEmailAction.invalid_link'));
      });

    return () => {
      active = false;
    };
  }, [firebase, oobCode, t]);

  useEffect(() => {
    if (status !== 'success') {
      return undefined;
    }

    const targetRoute = firebase.auth.currentUser ? ROUTES.HOME : ROUTES.SIGN_IN;
    const timeoutId = globalThis.setTimeout(() => {
      navigate(targetRoute, { replace: true });
    }, 800);

    return () => {
      globalThis.clearTimeout(timeoutId);
    };
  }, [firebase, navigate, status]);

  return (
    <main className='auth-main'>
      <div className='auth-left'>
        <div className='auth-card'>
          <h1>{t('verifyEmailAction.title')}</h1>
          <div className='auth-form'>
            {status === 'loading' ? (
              <p>{t('verifyEmailAction.processing')}</p>
            ) : null}
            {status === 'error' ? (
              <p className='auth-error'>{error}</p>
            ) : null}
            {status === 'success' ? (
              <p>{t('verifyEmailAction.success_message')}</p>
            ) : null}
            <Link to={ROUTES.SIGN_IN} role='button'>
              {t('verifyEmailAction.sign_in_button')}
            </Link>
            <Link to={ROUTES.HOME} role='button' className='secondary'>
              {t('verifyEmailAction.home_button')}
            </Link>
          </div>
        </div>
      </div>
      <div className='auth-right'>
        <img
          src='https://picsum.photos/1200/1200'
          alt={t('verifyEmailAction.image_alt')}
          loading='lazy'
          sizes='(max-width: 600px) 200px, (max-width: 1200px) 400px, 800px'
        />
      </div>
    </main>
  );
};

export default withFirebase(VerifyEmailActionPageBase);